import httpClient from "common/api/httpClient";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getResourcesStoreInfo } from "store/resourcesStore/resourcesStoreSlice";

const getSourcePath = (source: string) => {
  if (!source.toLowerCase().startsWith("http")) return source;
  let sourceUrl: URL;
  try {
    sourceUrl = new URL(source);
  } catch (e: unknown) {
    console.error(`Invlid URL '${source}'`, e);
    throw new Error("Invalid URL");
  }

  //extract base path to compare with current location.
  //Anchor's href without full path are considered relative to current location
  //so I need to detect this to extract the resource name
  //IE: <a href='myResource.png'>, href is "https://currentpath/myResource.png"
  //when I detect this, I need to extract myResource.png as it's the resource name
  const locationBase = window.location.pathname.substring(
    0,
    window.location.pathname.lastIndexOf("/")
  );
  const sourceLastSlash = sourceUrl.pathname.lastIndexOf("/");
  const sourceBase = sourceUrl.pathname.substring(0, sourceLastSlash);

  //if location and source share the same path, this is a resource
  if (
    locationBase === sourceBase &&
    sourceUrl.pathname.length > sourceLastSlash + 1
  ) {
    return sourceUrl.pathname.substring(sourceLastSlash + 1);
  }
  return undefined;
};
export const useResourcePath = () => {
  const dispatch = useAppDispatch();
  const resourcesStoreInfo = useAppSelector(
    (x) => x.resourcesStore.resourcesStoreInfo
  );

  //we consider expired 1 minute before actual expire date
  const checkIsExpired = useCallback(
    () =>
      !!(
        !resourcesStoreInfo ||
        (resourcesStoreInfo.expiresAt &&
          new Date(
            new Date(resourcesStoreInfo.expiresAt).getTime() - 60 * 1000
          ) <= new Date())
      ),
    [resourcesStoreInfo]
  );

  const getSharedFormToken = useCallback(async () => {
    const currentResourcesStoreInfo = checkIsExpired()
      ? await dispatch(getResourcesStoreInfo()).unwrap()
      : resourcesStoreInfo;
    const sharedFormToken = currentResourcesStoreInfo?.sharedFormToken!;

    return sharedFormToken;
  }, [checkIsExpired, dispatch, resourcesStoreInfo]);

  const getResourcePath = useCallback(
    async (source: string) => {
      const sourcePath = getSourcePath(source);

      if (!sourcePath || sourcePath.toLowerCase().startsWith("http"))
        return source;

      const baseUri = httpClient.getUri();

      const sharedFormToken = await getSharedFormToken();

      return `${baseUri}Resources/ResourcesStore/Download/${sourcePath}?sharedFormToken=${sharedFormToken}`;
    },
    [getSharedFormToken]
  );

  return getResourcePath;
};
